
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { usePromptManagement } from "@/hooks/usePromptManagement";
import { useUserManagement } from "@/hooks/useUserManagement";
import { useImportManagement } from "@/hooks/useImportManagement";

import Navbar from "@/components/layout/Navbar";
import AdminContainer from "@/components/admin/AdminContainer";
import AdminHeader from "@/components/admin/AdminHeader";
import AdminTabs from "@/components/admin/AdminTabs";
import AdminLoading from "@/components/admin/AdminLoading";

const Admin = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // Custom hooks for different functionality
  const { 
    pendingPrompts, 
    isLoading: isLoadingPrompts, 
    fetchPendingPrompts, 
    handleApprove, 
    handleReject, 
    handleDelete 
  } = usePromptManagement();
  
  const { users, isLoadingUsers, fetchUsers } = useUserManagement();
  
  const {
    isImporting,
    isImportingMarkdown,
    importStats,
    handleFileUpload,
    downloadTemplateFile,
    downloadMarkdownTemplate
  } = useImportManagement(user?.id || '', fetchPendingPrompts);

  const [totalCategories, setTotalCategories] = useState(0);
  const [reportedIssues, setReportedIssues] = useState(0);
  
  useEffect(() => {
    // Fetch total categories count (this would ideally come from an API)
    const fetchStats = async () => {
      // In a real app you'd fetch these from API endpoints
      // For now we'll use dummy data
      setTotalCategories(15);
      setReportedIssues(3);
    };
    
    fetchStats();
  }, []);

  // Redirect if not admin
  if (!user || !isAdmin) {
    navigate('/');
    return null;
  }

  // Loading state
  const isLoading = isLoadingPrompts && activeTab === "pending";
  if (isLoading) {
    return (
      <>
        <Navbar />
        <AdminLoading />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <AdminContainer>
        <AdminHeader 
          isImporting={isImporting}
          isImportingMarkdown={isImportingMarkdown}
          importStats={importStats}
          handleFileUpload={handleFileUpload}
          downloadTemplateFile={downloadTemplateFile}
          downloadMarkdownTemplate={downloadMarkdownTemplate}
        />
        
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-border/40 overflow-hidden animate-fade-in">
          <AdminTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            pendingPrompts={pendingPrompts}
            isLoadingPrompts={isLoadingPrompts}
            handleApprove={handleApprove}
            handleReject={handleReject}
            handleDelete={handleDelete}
            users={users}
            isLoadingUsers={isLoadingUsers}
            fetchUsers={fetchUsers}
            totalCategories={totalCategories}
            reportedIssues={reportedIssues}
          />
        </div>
      </AdminContainer>
    </>
  );
};

export default Admin;
